import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG = "/hero-pano.jpg?v=2";

const line1 = "Supercar";
const line2 = "&";
const line3 = "Classic Cars";

const revealParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.5 } },
};
const revealChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.2, 0.8, 0.2, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <div className="hero-pan-track">
          <img
            src={HERO_IMG}
            alt="Collezione di supercar classiche iconiche — Miura, F40, GT40, Countach, Ferrari 250 GT — in showroom elegante dai toni scuri"
            className="hero-pan-img"
            loading="eager"
          />
          <img src={HERO_IMG} alt="" aria-hidden="true" className="hero-pan-img" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative h-full max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 flex flex-col justify-end pt-28 sm:pt-32 pb-16 sm:pb-24"
      >
        <motion.h1
          variants={revealParent}
          initial="hidden"
          animate="show"
          className="font-serif font-light tracking-tighter leading-[0.85] text-white text-[13vw] sm:text-[11vw] md:text-[9vw] lg:text-[clamp(3.5rem,7vw,7.5rem)]"
          data-testid="hero-heading"
        >
          <span className="reveal-line"><motion.span variants={revealChild}>{line1}</motion.span></span>
          <span className="reveal-line text-neutral-400"><motion.span variants={revealChild}>{line2}</motion.span></span>
          <span className="reveal-line"><motion.span variants={revealChild}>{line3}</motion.span></span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="text-neutral-300 max-w-2xl font-light leading-relaxed whitespace-nowrap" style={{ fontSize: "clamp(0.6rem, 2.7vw, 1rem)" }}>
            {lang === "en" ? (
              <>
                Welcome.
                <br />
                Take a seat, fasten your seatbelt, hands on the wheel.
                <br />
                Here we go beyond «metal, aluminium or carbon fibre»
                <br />
                Together we'll discover the soul of your next car.
              </>
            ) : (
              <>
                Benvenuto.
                <br />
                Siediti, allaccia la cintura e mani sul volante.
                <br />
                Qui si va oltre al «metallo, alluminio o carbonio»
                <br />
                Scoprirai insieme a me l'anima della tua prossima automobile.
              </>
            )}
            <span
              className="block mt-4 label-eyebrow text-white/70 whitespace-nowrap"
              style={{ fontSize: "clamp(0.5rem, 2.5vw, 0.68rem)", letterSpacing: "0.15em" }}
            >
              {lang === "en" ? "Passion · Transparency · Professionalism" : "Passione · Trasparenza · Professionalità"}
            </span>
          </p>
          <div className="flex items-center gap-4">
            <a href="/vetture" className="btn-magnetic btn-solid" data-testid="hero-cta-vetture">
              {lang === "en" ? "The cars" : "Le vetture"} <ArrowRight size={16} />
            </a>
            <a href="#chi-sono" className="btn-magnetic" data-testid="hero-cta-about">
              {lang === "en" ? "About" : "Chi sono"}
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute right-6 sm:right-10 bottom-6 flex items-center gap-3 text-neutral-500"
      >
        <span className="label-eyebrow">Scroll</span>
        <span className="w-px h-10 bg-neutral-600 animate-pulse" />
      </motion.div>
    </section>
  );
}
